import React, { FC, useState, useCallback } from "react";
import classNames from "classnames/dedupe";

import { Button, LinkButton, SvgIcon, Typography } from "../../components";

import {
  CallSmallIcon,
  CheckmarkSmallIcon,
  DirectionSmallIcon,
  ExitIcon,
  HeartIcon,
  SoldOutSmallIcon,
} from "../../icons";
import { IStoreDetails } from "./StoreAvailability.interface";

const StoreDetails: FC<IStoreDetails> = ({
  title,
  distance,
  address,
  openingDetails,
  phoneNumber,
  quantity = null,
  location,
  isWithStoreFavourite = false,
  isWithSelectButton = false,
  labels = {
    detailsButton: "Store details",
    selectButton: "Select & Continue",
  },
  classes,
  userLocation,
  isActive,
  handleSelectStore,
  onSelectButtonClick,
  handleClose,
}) => {
  const [isPreferred, setIsPreferred] = useState<boolean>(false);

  const onSelectPreferredStore = () => {
    setIsPreferred(!isPreferred);
  };

  const rootClasses = classNames(
    "store",
    {
      "store-chosen": isActive,
    },
    classes?.root,
  );

  const favoriteButtonClasses = classNames(
    "button-link--wrapper add-to-favorite mt-7",
    {
      active: isPreferred,
    },
  );

  const startIcon = useCallback(() => <SvgIcon icon={CallSmallIcon} />, []);
  const directionStartIcon = useCallback(
    () => <SvgIcon icon={DirectionSmallIcon} />,
    [],
  );

  return (
    <div className={rootClasses} onClick={handleSelectStore}>
      <div className="store__data">
        <Typography classes={{ root: "mb-5" }} variant="h4-enriched" as="div">
          {title}
        </Typography>
        {distance && quantity ? (
          <div className="d-flex mb-5">
            <Typography classes={{ root: "store__distance" }} as="div">
              {distance}
            </Typography>
            {quantity > 0 ? (
              <Typography classes={{ root: "store__quantity" }} as="div">
                <SvgIcon icon={CheckmarkSmallIcon} />
                {quantity} in stock
              </Typography>
            ) : (
              <Typography
                classes={{ root: "store__quantity sold-out" }}
                as="div"
              >
                <SvgIcon icon={SoldOutSmallIcon} />
                sold out
              </Typography>
            )}
          </div>
        ) : null}

        <Typography classes={{ root: "store__address" }} as="div">
          {address}
        </Typography>

        <div className="d-flex flex-row">
          {openingDetails &&
            openingDetails.map((store, index) => {
              return (
                <Typography classes={{ root: "store__opening-hours" }} as="div">
                  <span>{store.openingDates}</span>
                  &nbsp;
                  <span>
                    {`(${store.openingHours})${
                      index < openingDetails.length - 1 ? ";" : ""
                    }`}
                    &nbsp;
                  </span>
                </Typography>
              );
            })}
        </div>

        <div className="d-flex mt-6">
          <div className="button-link--wrapper">
            <LinkButton
              as="a"
              href={`tel:${phoneNumber}`}
              target="_blank"
              variant="icon"
              startIcon={startIcon}
              classes={{
                root: "border-0",
                content: "text-uppercase font-weight-bold border-bottom",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              call
            </LinkButton>
          </div>
          <div className="button-link--wrapper ms-6">
            <LinkButton
              as="a"
              href={
                location
                  ? `https://www.google.com/maps?saddr=${
                      userLocation
                        ? `${userLocation.lat},${userLocation.lng}`
                        : "Current+Location"
                    }&daddr=${location.lat},${location.lng}`
                  : ""
              }
              target="_blank"
              variant="icon"
              startIcon={directionStartIcon}
              classes={{
                root: "border-0",
                content: "text-uppercase font-weight-bold border-bottom",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              directions
            </LinkButton>
          </div>
        </div>

        {isWithSelectButton && (
          <Button
            variant="conversion"
            classes={{ root: "w-100 w-md-auto mt-7" }}
            onClick={onSelectButtonClick}
          >
            {labels.selectButton}
          </Button>
        )}

        {isWithStoreFavourite && (
          <div
            className={favoriteButtonClasses}
            onClick={onSelectPreferredStore}
          >
            <HeartIcon />
            <LinkButton classes={{ root: "text-uppercase" }}>
              Add as preferred store
            </LinkButton>
          </div>
        )}
      </div>
      <Button classes={{ root: "btn btn-close" }}>
        <ExitIcon onClick={handleClose} />
      </Button>
    </div>
  );
};

export default StoreDetails;
